import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { ActivatedRoute } from '@angular/router';
import { AppService, PortalApp } from '../services/app';
import { Application, ProfileConfig, SelfService, SelfServiceComponent } from 'sigesr-self-service-lib';
import { environment } from '../../environments/environment';

@Component({
  selector: 'home-sigesr',
  standalone: true,
  imports: [CommonModule, RouterModule, SelfServiceComponent],
  templateUrl: './home-sigesr.html',
  styleUrls: ['./home-sigesr.scss']
})
export class HomeSigesrComponent implements OnInit {
  // Liste des applications avec leur statut d'accès
  allAppsWithStatus: (PortalApp & { disabled: boolean })[] = [];
  
  // Informations utilisateur
  userName = '';
  userEmail = '';
  userRole = '';
  lastConnection = '';
  accessDeniedApp: string | null = null;
  
  isLoading = true;
  isLoadingUser = true;

  currentApp: Application | null = null;
  currentUser: any = null;

  profileConfig: ProfileConfig = {
    showProfil: true,
    showChangePassword: true,
    avatarBackgroundColor: "#45B56F",
    showLogout: true,
    showAppSelector: false,
    maxVisibleApps: 9,
    appBuilder: {
      currentApp: environment.keycloak.clientId,
      environment: 'prod',
    }
  };

  constructor(
    private keycloak: KeycloakService,
    private appService: AppService,
    private route: ActivatedRoute,
    private selfService: SelfService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  async ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        await this.keycloak.login({ redirectUri: window.location.origin });
        return;
      }

      await Promise.all([
        this.loadUserInfo(),
        this.loadAllAppsWithAccess()
      ]);

      this.checkAccessDenied();
    } catch (error) {
      console.error('[Home] Erreur init:', error);
    } finally {
      this.isLoading = false;
      this.isLoadingUser = false;
      this.cdr.detectChanges();
    }
  }

  private async loadUserInfo() {
    try {
      const profile = await this.keycloak.loadUserProfile();
      this.userName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || 'Utilisateur';
      this.userEmail = profile.email || 'Non disponible';
      const roles = await this.appService.getUserRoles();
      this.userRole = this.determineMainRole(roles);
      this.lastConnection = this.formatLastConnection();
      
      this.currentUser = {
        ...profile,
        roles,
        mainRole: this.userRole,
        lastConnection: this.lastConnection,
        fullname: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || 'Utilisateur'
      };
      this.selfService.setUserProfile(this.currentUser);
    } catch (error) {
      console.error('Erreur chargement utilisateur', error);
      this.currentUser = {
        fullname: 'Utilisateur',
        lastConnection: this.formatLastConnection()
      };
      this.userName = 'Utilisateur';
      this.userEmail = 'Non disponible';
      this.userRole = 'Utilisateur';
      this.lastConnection = this.formatLastConnection();
    }
  }

  private determineMainRole(roles: string[]): string {
    if (roles.some(r => r.includes('SUPER_ADMIN'))) return 'Super Administrateur';
    if (roles.some(r => r.includes('ADMIN'))) return 'Administrateur';
    if (roles.some(r => r.includes('EDITOR'))) return 'Editeur';
    return 'Utilisateur';
  }

  private formatLastConnection(): string {
    return new Date().toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  private checkAccessDenied() {
    this.route.queryParams.subscribe(params => {
      if (params['access_denied']) {
        this.accessDeniedApp = params['app'] || 'une application';
        setTimeout(() => window.history.replaceState({}, '', window.location.pathname), 100);
      }
    });
  }

/**
 * Charge toutes les applications et fusionne avec les applications accessibles
 * pour définir le statut disabled, puis trie pour placer les actives en premier.
 */
private async loadAllAppsWithAccess() {
  try {
    this.isLoading = true;
    
    const accessibleApps = await this.appService.getAccessibleApps();
    const accessibleClientIds = new Set(accessibleApps.map(app => app.clientId));
    
    const allApps = this.appService.getAllApps();
    
    this.allAppsWithStatus = allApps.map(app => ({
      ...app,
      disabled: !accessibleClientIds.has(app.clientId)
    }));

    this.allAppsWithStatus.sort((a, b) => {
      if (a.disabled === b.disabled) return 0;
      return a.disabled ? 1 : -1;
    });
    
    console.log('[Home] Apps chargées et triées:', this.allAppsWithStatus.length);
  } catch (error) {
    console.error('[Home] Erreur chargement des apps:', error);
    const allApps = this.appService.getAllApps();
    this.allAppsWithStatus = allApps.map(app => ({
      ...app,
      disabled: true
    }));

    // Même tri en cas d'erreur (mais ici toutes sont disabled)
    this.allAppsWithStatus.sort((a, b) => {
      if (a.disabled === b.disabled) return 0;
      return a.disabled ? 1 : -1;
    });
  }
}

  // Gestion des applications (pour la librairie self‑service)
  handleAppSelected(app: Application) {
    this.currentApp = app;
    this.selfService.setCurrentApp(app);
    this.profileConfig.currentAppId = app.id;
    if (app.url) this.router.navigate([app.url]);
  }

  handleChangePassword(): void {
    alert('Ouverture du formulaire de changement de mot de passe...');
  }

  handleLogout(): void {
    if (confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      try {
        this.keycloak.logout(window.location.origin);
      } catch (error) {
        console.error('Erreur déconnexion', error);
      }
    }
    localStorage.clear();
  }

  handleShowAllApps() { }
}